import { HeroSection, SectionSubtitle, SectionTitle } from '@web_opxp/components';
import { DateUtils, FirebaseActions, useTranslation } from '@web_opxp/features';
import React from 'react';
import { connect, useSelector } from 'react-redux';
import { SKELETON_CLASSNAME } from '../../base/constants';
import SideBarSection from '../../components/SideBarSection/SideBarSection';
import { GroupAdminBanner } from '../../groupAdmin';
import { IState } from '../../store/state';
import EvaluationSideBar from '../EvaluationSideBar';
import * as Selectors from '../selectors';
import { EvaluationActions } from '../slice';
import useUserIdFromRoute from '../useUserIdFromRoute';
import { getFirstAndLastDayTimestamps } from '../utils';
import CalendarSection from './CalendarSection';
import DataExportSection from './DataExportSection';
import StatisticsSection from './StatisticsSection';

interface IOverviewPageParams {
  loadProgress: (startTimestamp: number, endTimestamp: number, userId?: number) => void;
  onTrackCalendarShow: () => void;
}

export const TrainingOverviewPageFC: React.FC<IOverviewPageParams> = ({ loadProgress, onTrackCalendarShow }) => {
  const { language, t } = useTranslation();

  const ready = useSelector(Selectors.isFeatureReady);
  const userId = useUserIdFromRoute();

  const { firstDayTimestamp, lastDayTimestamp } = getFirstAndLastDayTimestamps(new Date().getTime());
  const [startTimestamp, setStartTimestamp] = React.useState(firstDayTimestamp);
  const [endTimestamp, setEndTimestamp] = React.useState(lastDayTimestamp);

  // Load the current month's progress for selected or default user
  React.useEffect(() => {
    loadProgress(DateUtils.getUnixTimestamp(startTimestamp), DateUtils.getUnixTimestamp(endTimestamp), userId);
  }, [loadProgress, startTimestamp, endTimestamp, userId]);
  // BW-7761 track rendering of the calendar section for non GAD user
  React.useEffect(() => {
    if (!userId && ready && onTrackCalendarShow) {
      onTrackCalendarShow();
    }
  }, [onTrackCalendarShow, ready, userId]);

  const onNavigate = (navigateStartTimestamp: number) => {
    const { firstDayTimestamp, lastDayTimestamp } = getFirstAndLastDayTimestamps(navigateStartTimestamp);
    setStartTimestamp(firstDayTimestamp);
    setEndTimestamp(lastDayTimestamp);
  };

  const subtitle = userId ? t('Evaluation.Overview.SubTitle.User') : t('Evaluation.Overview.SubTitle.Self');

  const evaluationLottie = 'Lab_Animation360';
  const loaded = ready && !!language;
  const skeletonClass = loaded ? '' : SKELETON_CLASSNAME;
  const sidebar = <EvaluationSideBar />;

  return (
    <>
      <HeroSection lottie={evaluationLottie} className="evaluation-section">
        <SectionTitle className={skeletonClass}>{t('Evaluation.Overview.PageTitle')}</SectionTitle>
        <SectionSubtitle className={skeletonClass}>{subtitle}</SectionSubtitle>
      </HeroSection>
      <GroupAdminBanner userId={userId} />
      <SideBarSection sidebar={sidebar}>
        <div className="col-1"></div>
        <div className="col-7">
          <CalendarSection
            userId={userId}
            startTimestamp={startTimestamp}
            endTimestamp={endTimestamp}
            onNavigate={onNavigate}
          />
          <StatisticsSection userId={userId} />
          <DataExportSection />
        </div>
        <div className="col-1"></div>
      </SideBarSection>
    </>
  );
};

const mapStateToProps = (state: IState) => {
  const ready = Selectors.areHighscoresReady(state);
  return {
    ready,
  };
};

export const mapDispatchToProps = {
  loadProgress: EvaluationActions.loadProgressHistory,
  onTrackCalendarShow: () => FirebaseActions.analyticsEventAction({ eventName: 'v_evaluation_view' }),
};

const TrainingOverviewPage = connect(mapStateToProps, mapDispatchToProps)(TrainingOverviewPageFC);
export default TrainingOverviewPage;
